// ─────────────────────────────────────────────────────────────────────────────
// DOC-LIFECYCLE-001 (Phase G) — PURE Ableitungen für die Partner-Dokumentseite.
//
// Fachliches Zustandsmodell (überall gleich, wie Backend/Admin):
//   missing | capturePreview | uploading | pendingReview | approved | rejected(grund)
//   | renewalPending | expired   (+ complianceLocked als Backend-Sperre)
//
// capturePreview/uploading sind reine CLIENT-Phasen während eines laufenden Uploads —
// alle SERVER-Zustände kommen ausschließlich aus dem Backend-Vertrag:
//   GET /partners/onboarding → documents[] je Katalog-Punkt mit
//   { type, label, status, done, lifecycle_status, effective, latest, rejection_reason,
//     expires_at, renewal:{open,revision}, compliance_locked, legacy_hint? }.
//
// WICHTIG (Regel „Interna nie nach außen" analog): das Frontend führt KEINE eigene
// Pflichtliste mehr — welche Punkte existieren und Pflicht sind, bestimmt allein das
// Backend (documentCatalog/onboarding). Hier gibt es nur:
//   • die Zustandsableitung je Punkt (deriveItemView),
//   • die Upload-WEG-Zuordnung (welcher document_type gehört zu einem Katalog-Schlüssel —
//     Alternativ-Gruppe Gewerbe/Handelsregister, Bank-Nachweis), OHNE Pflicht-Semantik,
//   • deutsche Anzeigenamen. Alles pur und deterministisch testbar.
// ─────────────────────────────────────────────────────────────────────────────

// Upload-Wege je Katalog-Schlüssel. KEINE Pflichtliste: nur die Zuordnung, unter welchem
// document_type ein Backend-Punkt hochgeladen wird (Spiegel der anyOf-Gruppen des
// Backend-Katalogs; unbekannte Punkte laden unter ihrem eigenen Schlüssel hoch).
const UPLOAD_OPTIONS = {
  // Alternativ-Gruppe: EINE von beiden Urkunden erfüllt den Punkt.
  gewerbe_or_handelsregister: [
    { type: 'gewerbeanmeldung', label: 'Gewerbeanmeldung' },
    { type: 'handelsregister', label: 'Handelsregister' },
  ],
  // Gemeinsamer Prüfkandidat: IBAN (PATCH /partners/me/bank) + Nachweis-Upload zusammen.
  bank: [{ type: 'bank_nachweis', label: 'Bank-/Inhabernachweis' }],
};

// Konzession braucht beim Upload (auch bei der ERNEUERUNG) Ausstellungs- UND Ablaufdatum
// (Backend-Katalog: issuedRequired + expiresRequired; Wire-Felder issue_date + expires_at —
// der Server validiert zusätzlich mit 400 issued_required/expiry_required).
export const needsIssuedAndExpiry = (key) => key === 'konzession';

// Server-lifecycle_status eines Katalog-Punkts → UI-Zustand.
// Vorrang: Sperre > (approved + offene Erneuerung) > Einzelzustand.
// Bei 'expired' + offener Erneuerung bleibt der Punkt 'expired' (die Sperr-/Gate-Wahrheit),
// die Einreichung bleibt über renewalOpen sichtbar — pending heißt NUR eingereicht.
export function deriveItemView(item) {
  const it = item || {};
  const ls = it.lifecycle_status || it.status || 'missing';
  const renewalOpen = !!(it.renewal && it.renewal.open);
  let state;
  if (it.compliance_locked || ls === 'compliance_locked') state = 'complianceLocked';
  else if (ls === 'approved') state = renewalOpen ? 'renewalPending' : 'approved';
  else if (ls === 'expired') state = 'expired';
  else if (ls === 'pending') state = 'pendingReview';
  else if (ls === 'rejected') state = 'rejected';
  else state = 'missing';
  return {
    key: it.type,
    label: it.label || it.type,
    state,
    renewalOpen,
    rejectionReason: it.rejection_reason || null,
    expiresAt: it.expires_at || null,
    effective: it.effective || null,   // wirksame (freigegebene) Revision — GETRENNT von latest
    latest: it.latest || null,         // neueste Einreichung — niemals als „freigegeben" darstellen
    legacyHint: it.legacy_hint || null,
    isBank: it.type === 'bank',
    done: !!it.done,
  };
}

// Checkliste = EXAKT die Backend-Punkte, in Backend-Reihenfolge. Nichts filtern, nichts erfinden.
export function buildChecklist(onboardingDocuments) {
  return (onboardingDocuments || []).map(deriveItemView);
}

// Upload-Wege eines Punkts (siehe UPLOAD_OPTIONS; Fallback: eigener Schlüssel).
export function uploadOptionsFor(view) {
  return UPLOAD_OPTIONS[view.key] || [{ type: view.key, label: view.label }];
}

// Deutsche Anzeigenamen der Dokument-lifecycle_status-Werte (Vertrag Stufe A, inkl. superseded).
const LIFECYCLE_LABELS = {
  pending_review: 'In Prüfung',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
  superseded: 'Ersetzt',
  expired: 'Abgelaufen',
};
export const lifecycleLabel = (lifecycleStatus) => LIFECYCLE_LABELS[lifecycleStatus] || lifecycleStatus || '–';

// Darstellung je UI-Zustand (Farbsystem: Grau/Schwarz + Rot Fehler + Violett CI).
const PRESENTATION = {
  missing:          { label: 'Fehlt',                 color: '#6b7280', bg: '#f3f4f6' },
  capturePreview:   { label: 'Vorschau',              color: '#6b7280', bg: '#f3f4f6' },
  uploading:        { label: 'Wird hochgeladen…',     color: '#6b7280', bg: '#f3f4f6' },
  pendingReview:    { label: 'In Prüfung',            color: '#6b7280', bg: '#f3f4f6' },
  approved:         { label: 'Freigegeben',           color: '#9C27B0', bg: '#F3E5F5' },
  rejected:         { label: 'Abgelehnt',             color: '#dc2626', bg: '#fef2f2' },
  renewalPending:   { label: 'Erneuerung in Prüfung', color: '#6b7280', bg: '#f3f4f6' },
  expired:          { label: 'Abgelaufen',            color: '#dc2626', bg: '#fef2f2' },
  complianceLocked: { label: 'Gesperrt',              color: '#dc2626', bg: '#fef2f2' },
};
export const statusPresentation = (state) => PRESENTATION[state] || PRESENTATION.missing;

// effective- und latest-Revision GETRENNT als Anzeige-Zeilen. Gleiche Revision (id) nur
// einmal (als 'effective'); eine neuere Einreichung erscheint IMMER als eigene Zeile mit
// ihrem eigenen (nicht freigegebenen) Status.
export function revisionRows(view) {
  const rows = [];
  if (view.effective) {
    rows.push({
      kind: 'effective',
      documentId: view.effective.id,
      revision: view.effective.revision || 1,
      statusLabel: 'Wirksam freigegeben',
      expiresAt: view.effective.expires_at || null,
    });
  }
  if (view.latest && (!view.effective || view.latest.id !== view.effective.id)) {
    rows.push({
      kind: 'latest',
      documentId: view.latest.id,
      revision: view.latest.revision || 1,
      statusLabel: lifecycleLabel(view.latest.lifecycle_status || (view.latest.status === 'pending' ? 'pending_review' : view.latest.status)),
      expiresAt: view.latest.expires_at || null,
    });
  }
  return rows;
}

// Beschriftung des Upload-Knopfs je Zustand (Erneuern vs. Neu hochladen vs. Ersetzen).
export function uploadActionLabel(state) {
  if (state === 'approved' || state === 'expired') return 'Erneuern';
  if (state === 'rejected') return 'Neu hochladen';
  if (state === 'pendingReview' || state === 'renewalPending') return 'Ersetzen';
  return 'Hochladen';
}
